import { yupResolver } from '@hookform/resolvers/yup'
import { Close } from '@mui/icons-material'
import {
  CardActions,
  CardContent,
  Divider,
  Grid,
  Typography
} from '@mui/material'
import {
  Button,
  Card,
  CardHeader,
  IconButton,
  Modal,
  TextFieldC
} from 'components'
import { FormProvider, useForm } from 'react-hook-form'
import { Yup } from 'utils'
import { Attachments } from 'modules/orders/components/Tracking/Attachments'
import { FileUploadProvider, useFiles } from 'contexts'
import fileSize from 'filesize'
import { ATTACHMENT_SIZE_LIMIT } from 'config'
import { useSendSignedProposalStore } from './sendSignedProposalStore'
import { useSendSignedProposal } from './useSendSignedProposal'
import { SignedProposals } from '../signedProposals'
import { AutentiqueResponse } from '../signedProposals/useLoadAutentiqueSignedProposals'
import { useConfirmSignature } from '../signedProposals/useConfirmSignature'

type FormData = {
  title: { id: string; name: string }
  subject: string
  datePlanned: string
  dateHeld: string
  text: string
  emailCc: { id: string; value: string }[]
  email: { id: string; value: string }[]
  sendEmail: boolean
}

const schema = Yup.object({
  text: Yup.string()
})

function SignedProposalModalProvided() {
  const { uploadedFiles: trackingFiles } = useFiles()
  const isOpen = useSendSignedProposalStore.useIsModalOpen()
  const setIsOpenModal = useSendSignedProposalStore.useSetModalOpen()
  const proposalId = useSendSignedProposalStore.useProposalId()

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      text: ''
    }
  })

  const { handleSubmit, reset } = methods

  const { mutateAsync, isLoading } = useSendSignedProposal()
  const { mutateAsync: confirmSignature, isLoading: confirmLoading } =
    useConfirmSignature()

  const onClose = () => {
    setIsOpenModal(false)
  }

  const onSubmit = async (data: FormData) => {
    try {
      const normalizedData = {
        files: trackingFiles.map((file) => file.path),
        text: data.text,
        proposalId: proposalId
      }
      await mutateAsync(normalizedData)
      setIsOpenModal(false)
      reset()
    } catch {}
  }

  const onSignedClick = async (item: AutentiqueResponse) => {
    try {
      const file = await confirmSignature({ url: item.files.signed })
      const normalizedData = {
        files: [file],
        text: ' ',
        proposalId: proposalId
      }
      await mutateAsync(normalizedData)
      setIsOpenModal(false)
      reset()
    } catch {}
  }

  const uploading = trackingFiles.some(
    (item) => (item?.progress || 0) < 100 && !item.uploaded
  )

  const isNotUploadedItem =
    trackingFiles.some((item) => !item.uploaded) || !trackingFiles.length
  const totalSize = trackingFiles.reduce(
    (acc, cur) => acc + (cur?.file?.size || 0),
    0
  )

  const isExceededSizeLimit = totalSize >= ATTACHMENT_SIZE_LIMIT

  return (
    <FormProvider {...methods}>
      <Modal
        open={isOpen}
        onClose={onClose}
        keepMounted={false}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }}
      >
        <Card style={{ padding: 0, minWidth: 300, width: 600 }}>
          <CardHeader
            title={`Anexar orçamento assinado: ${proposalId || ''}`}
            HeaderRight={
              <IconButton onClick={() => onClose()}>
                <Close />
              </IconButton>
            }
          />
          <Divider />

          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextFieldC name="text" label="Texto" multiline rows={4} />
              </Grid>

              <Grid item xs={12}>
                <Attachments multiple={true} limitFiles={15} />
              </Grid>
              {isExceededSizeLimit && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="error">
                    A soma dos tamanhos de cada arquivo deve ser menor que{' '}
                    {fileSize(ATTACHMENT_SIZE_LIMIT)}!
                  </Typography>
                </Grid>
              )}
            </Grid>

            <SignedProposals
              proposalId={proposalId}
              onSignedClick={onSignedClick}
              disableConfirmButton
            />
          </CardContent>
          <Divider />
          <CardActions>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '1rem'
              }}
            >
              <Button
                variant="text"
                sx={{
                  width: { sm: 100, md: 200 }
                }}
                onClick={() => onClose()}
              >
                Cancelar
              </Button>
              <Button
                sx={{
                  width: { sm: 100, md: 200 }
                }}
                type="button"
                disabled={
                  uploading ||
                  isNotUploadedItem ||
                  isLoading ||
                  isExceededSizeLimit ||
                  confirmLoading
                }
                onClick={handleSubmit(onSubmit)}
              >
                Cadastrar
              </Button>
            </div>
          </CardActions>
        </Card>
      </Modal>
    </FormProvider>
  )
}

export const SendSignedProposalModal = () => (
  <FileUploadProvider>
    <SignedProposalModalProvided />
  </FileUploadProvider>
)

export default SendSignedProposalModal
